import {
    Select,
    Sheet,
    Option,
    FormLabel,
    FormControl,
    Box,
    Typography,
    Input,
    Stack,
    Table,
    Button
} from "@mui/joy";
import {useState} from "react";
import {addStyles, StaticMathField} from "react-mathquill";
import {
    SaturatedWaterMixPressure,
    SaturatedWaterMixTemperature,
    SubcooledWater,
    SuperheatedSteam
} from "../propertydata/water";
import {SaturatedR22MixPressure, SaturatedR22MixTemperature, SubcooledR22, SuperheatedR22} from "../propertydata/r22";

addStyles();

export default function App() {
    const [substance, setSubstance] = useState("water");
    const [pressure, setPressure] = useState("");
    const [temperature, setTemperature] = useState("");
    const [quality, setQuality] = useState("");
    const [volume, setVolume] = useState("");
    const [enthalpy, setEnthalpy] = useState("");
    const [internalEnergy, setInternalEnergy] = useState("");
    const [entropy, setEntropy] = useState("");

    const [initialData, setInitialData] = useState(null);
    const [mainInterpolationData, setMainInterpolationData] = useState(null);
    const [state, setState] = useState(null); // -1 = liq, 0 = mix, 1 = gas

    const clearValues = () => {
        setPressure("");
        setTemperature("");
        setQuality("");
        setVolume("");
        setEnthalpy("");
        setInternalEnergy("");
        setEntropy("");
        setInitialData(null);
        setMainInterpolationData(null);
    };

    const findBoundingRows = (table, rowIndex, value) => {
        if ((Math.abs(table[0][rowIndex] - value) / value) <= 0.01) {
            return [table[0]];
        }

        for (let i = 0; i < table.length - 1; ++i) {
            const lowerRow = table[i];
            const upperRow = table[i + 1];

            if ((Math.abs(upperRow[rowIndex] - value) / value) <= 0.01) {
                return [upperRow];
            }

            if (value < upperRow[rowIndex] && value > lowerRow[rowIndex]) {
                return [
                    lowerRow,
                    upperRow
                ];
            }
        }

        return [];
    };

    const runInterpolation = () => {
        const tables = {
            water: {
                lvmixPressure: SaturatedWaterMixPressure,
                lvmixTemperature: SaturatedWaterMixTemperature,
                superheat: SuperheatedSteam,
                subcool: SubcooledWater,
            },
            r22: {
                lvmixPressure: SaturatedR22MixPressure,
                lvmixTemperature: SaturatedR22MixTemperature,
                superheat: SuperheatedR22,
                subcool: SubcooledR22,
            },
        };

        const lvmixPressureTable = tables[substance].lvmixPressure;
        const lvmixTemperatureTable = tables[substance].lvmixTemperature;
        const superheatTable = tables[substance].superheat;
        const subcoolTable = tables[substance].subcool;

        let originalRows = null;
        let initialInterpolationFactor = null;
        let finalRowData = null;
        let initialData = {};

        if (pressure !== "") {
            const pressureString = pressure.trim().toLowerCase();
            let pressureValueBar = 0;

            if (pressureString.endsWith('bar')) {
                pressureValueBar = parseFloat(pressureString.slice(0, -3));
            }
            else if (pressureString.endsWith('mpa')) {
                pressureValueBar = parseFloat(pressureString.slice(0, -3)) * 10;
            }
            else if (pressureString.endsWith('kpa')) {
                pressureValueBar = parseFloat(pressureString.slice(0, -3)) / 10;
            }
            else {
                pressureValueBar = parseFloat(pressureString);
            }

            originalRows = findBoundingRows(lvmixPressureTable, 0, pressureValueBar);

            if (originalRows.length === 1) {
                finalRowData = originalRows[0];
            }
            else {
                initialInterpolationFactor = (pressureValueBar - originalRows[0][0]) / (originalRows[1][0] - originalRows[0][0]);

                finalRowData = originalRows[0].map((value, index) => (
                    initialInterpolationFactor * (originalRows[1][index] - value) + value
                ));
            }

            initialData = {
                originalRows,
                pressureValueBar,
                initialInterpolationFactor,
                finalRowData,
            };
        }

        if (temperature !== "") {
            const temperatureString = temperature.trim().toLowerCase();
            let temperatureValueK = 0;

            if (temperatureString.endsWith('c')) {
                temperatureValueK = parseFloat(temperatureString.slice(0, -1)) + 273.15;
            }
            else if (temperatureString.endsWith('k')) {
                temperatureValueK = parseFloat(temperatureString.slice(0, -1));
            }
            else {
                temperatureValueK = parseFloat(temperatureString);
            }

            if (initialData?.pressureValueBar !== undefined) {
                initialData.temperatureValueK = temperatureValueK;
            }
            else {
                originalRows = findBoundingRows(lvmixTemperatureTable, 1, temperatureValueK);

                if (originalRows.length === 1) {
                    finalRowData = originalRows[0];
                }
                else {
                    initialInterpolationFactor = (temperatureValueK - originalRows[0][1]) / (originalRows[1][1] - originalRows[0][1]);

                    finalRowData = originalRows[0].map((value, index) => (
                        initialInterpolationFactor * (originalRows[1][index] - value) + value
                    ));
                }

                initialData = {
                    originalRows,
                    temperatureValueK,
                    initialInterpolationFactor,
                    finalRowData,
                };
            }
        }

        setInitialData(initialData);

        let anchor = {};
        let qualityValue = undefined;

        let supersubLookup =  null;

        if (quality !== '') {
            qualityValue = parseFloat(quality);

            if (qualityValue > 1) {
                qualityValue /= 100;
            }

            anchor.quality = qualityValue;
        }
        else if (volume !== '') {
            const specificVolumeValue = parseFloat(volume);

            qualityValue = (specificVolumeValue - finalRowData[2]) / (finalRowData[3] - finalRowData[2]);
            anchor.volume = specificVolumeValue;
            supersubLookup = { index: 2, value: specificVolumeValue };
        }
        else if (internalEnergy !== '') {
            const internalEnergyValue = parseFloat(internalEnergy);

            qualityValue = (internalEnergyValue - finalRowData[4]) / (finalRowData[4] - finalRowData[3]);
            anchor.internalEnergy = internalEnergyValue;
            supersubLookup = { index: 3, value: internalEnergyValue };
        }
        else if (enthalpy !== '') {
            const specificEnthalpyValue = parseFloat(enthalpy);

            qualityValue = (specificEnthalpyValue - finalRowData[6]) / (finalRowData[7] - finalRowData[6]);
            anchor.specificEnthalpy = specificEnthalpyValue;
            supersubLookup = { index: 4, value: specificEnthalpyValue };
        }
        else if (entropy !== '') {
            const specificEntropyValue = parseFloat(entropy);

            qualityValue = (specificEntropyValue - finalRowData[8]) / (finalRowData[9] - finalRowData[8]);
            anchor.specificEntropy = specificEntropyValue;
            supersubLookup = { index: 5, value: specificEntropyValue };
        }

        let interpolationData = null;

        if (qualityValue && qualityValue >= 0 && qualityValue <= 1) {
            setState(0);

            interpolationData = {
                anchor,
                qualityValue,
                finalRowData,
                specificVolume: qualityValue * (finalRowData[3] - finalRowData[2]) + finalRowData[2],
                specificEnthalpy: qualityValue * (finalRowData[7] - finalRowData[6]) + finalRowData[6],
                specificInternalEnergy: qualityValue * (finalRowData[5] - finalRowData[4]) + finalRowData[4],
                specificEntropy: qualityValue * (finalRowData[9] - finalRowData[8]) + finalRowData[8],
            };

            setQuality(interpolationData.qualityValue.toPrecision(5));
        }
        else {
            let mainTable = [];

            if (qualityValue > 1 || (initialData.temperatureValueK !== undefined && initialData.temperatureValueK > initialData.finalRowData[1])) {
                setState(1);
                mainTable = superheatTable;
            }
            else if (qualityValue < 0 || (initialData.temperatureValueK !== undefined && initialData.temperatureValueK < initialData.finalRowData[1])) {
                setState(-1);
                mainTable = subcoolTable;
            }

            if (initialData.temperatureValueK) {
                supersubLookup = { index: 1, value: initialData.temperatureValueK };
                anchor = { temperature: initialData.temperatureValueK };
            }

            if (supersubLookup === null) {
                return;
            }

            if (initialData?.pressureValueBar) {
                const pressureBoundRows = findBoundingRows(mainTable, 0, initialData.pressureValueBar);

                if (pressureBoundRows?.length === 2) {
                    const pressureLowSet = mainTable.filter((row) => row[0] === pressureBoundRows[0][0]);
                    const pressureHighSet = mainTable.filter((row) => row[0] === pressureBoundRows[1][0]);

                    const lowerPressureFinalRows = findBoundingRows(pressureLowSet, supersubLookup.index, supersubLookup.value);
                    const upperPressureFinalRows = findBoundingRows(pressureHighSet, supersubLookup.index, supersubLookup.value);

                    let lowerPressureInterpolationFactor = 0;
                    let upperPressureInterpolationFactor = 0;
                    let lowerPressureRow = null;
                    let upperPressureRow = null;

                    if (lowerPressureFinalRows.length > 1) {
                        lowerPressureInterpolationFactor = (supersubLookup.value - lowerPressureFinalRows[0][supersubLookup.index]) / (lowerPressureFinalRows[1][supersubLookup.index] - lowerPressureFinalRows[0][supersubLookup.index]);
                        lowerPressureRow = lowerPressureFinalRows[0].map((value, index) => lowerPressureInterpolationFactor * (lowerPressureFinalRows[1][index] - value) + value);
                    }
                    else {
                        lowerPressureRow = lowerPressureFinalRows[0];
                        lowerPressureFinalRows.push([0, 0, 0, 0, 0, 0]);
                    }

                    if (upperPressureFinalRows.length > 1) {
                        upperPressureInterpolationFactor = (supersubLookup.value - upperPressureFinalRows[0][supersubLookup.index]) / (upperPressureFinalRows[1][supersubLookup.index] - upperPressureFinalRows[0][supersubLookup.index]);
                        upperPressureRow = upperPressureFinalRows[0].map((value, index) => upperPressureInterpolationFactor * (upperPressureFinalRows[1][index] - value) + value);
                    }
                    else {
                        upperPressureRow = upperPressureFinalRows[0];
                        upperPressureFinalRows.push([0, 0, 0, 0, 0, 0]);
                    }

                    const finalInterpolationFactor = (initialData.pressureValueBar - lowerPressureRow[0]) / (upperPressureRow[0] - lowerPressureRow[0]);

                    const finalRowData = lowerPressureRow.map((value, index) => finalInterpolationFactor * (upperPressureRow[index] - value) + value);

                    interpolationData = {
                        base: 'pressure',
                        rows: [...lowerPressureFinalRows, ...upperPressureFinalRows],
                        intermediateRows: [lowerPressureRow, upperPressureRow],
                        supersubLookup,
                        lowerInterpolationFactor: upperPressureInterpolationFactor,
                        upperInterpolationFactor: lowerPressureInterpolationFactor,
                        finalInterpolationFactor,
                        finalRowData,
                        anchor,
                        specificVolume: finalRowData[2],
                        specificInternalEnergy: finalRowData[3],
                        specificEnthalpy: finalRowData[4],
                        specificEntropy: finalRowData[5],
                    };
                }
                else if (pressureBoundRows?.length === 1) {
                    const pressureSet = mainTable.filter((row) => row[0] === pressureBoundRows[0][0]);

                    if (supersubLookup) {
                        const boundingRows = findBoundingRows(pressureSet, supersubLookup.index, supersubLookup.value);

                        if (boundingRows?.length === 2) {
                            // Pressure match, interpolate between two rows.

                            const interpolationFactor = (supersubLookup.value - boundingRows[0][supersubLookup.index]) / (boundingRows[1][supersubLookup.index] - boundingRows[0][supersubLookup.index]);
                            const finalRowData = boundingRows[0].map((value, index) => interpolationFactor * (boundingRows[1][index] - value) + value);

                            interpolationData = {
                                rows: boundingRows,
                                supersubLookup,
                                anchor,
                                interpolationFactor,
                                finalRowData,
                                specificVolume: finalRowData[2],
                                specificInternalEnergy: finalRowData[3],
                                specificEnthalpy: finalRowData[4],
                                specificEntropy: finalRowData[5],
                            };
                        }
                        else if (boundingRows?.length === 1) {
                            // Exact match found.

                            interpolationData = {
                                rows: boundingRows,
                                finalRowData: boundingRows[0],
                                specificVolume: boundingRows[0][2],
                                specificInternalEnergy: boundingRows[0][3],
                                specificEnthalpy: boundingRows[0][4],
                                specificEntropy: boundingRows[0][5],
                            };
                        }
                        else {
                            return;
                        }
                    }
                }
                else {
                    return;
                }
            }
            else if (initialData.temperatureValueK) {
                const temperatureBoundRows = findBoundingRows(mainTable.sort((a, b) => a[1] - b[1]), 1, initialData.temperatureValueK);

                if (temperatureBoundRows?.length === 2) {
                    const temperatureLowSet = mainTable.filter((row) => row[1] === temperatureBoundRows[0][1]);
                    const temperatureHighSet = mainTable.filter((row) => row[1] === temperatureBoundRows[1][1]);

                    const lowerTemperatureFinalRows = findBoundingRows(temperatureLowSet, supersubLookup.index, supersubLookup.value);
                    const upperTemperatureFinalRows = findBoundingRows(temperatureHighSet, supersubLookup.index, supersubLookup.value);

                    let lowerTemperatureInterpolationFactor = 0;
                    let upperTemperatureInterpolationFactor = 0;
                    let lowerTemperatureRow = null;
                    let upperTemperatureRow = null;

                    if (lowerTemperatureFinalRows.length > 1) {
                        lowerTemperatureInterpolationFactor = (supersubLookup.value - lowerTemperatureFinalRows[0][supersubLookup.index]) / (lowerTemperatureFinalRows[1][supersubLookup.index] - lowerTemperatureFinalRows[0][supersubLookup.index]);
                        lowerTemperatureRow = lowerTemperatureFinalRows[0].map((value, index) => lowerTemperatureInterpolationFactor * (lowerTemperatureFinalRows[1][index] - value) + value);
                    }
                    else {
                        lowerTemperatureRow = lowerTemperatureFinalRows[0];
                        lowerTemperatureFinalRows.push([0, 0, 0, 0, 0, 0]);
                    }

                    if (upperTemperatureFinalRows.length > 1) {
                        upperTemperatureInterpolationFactor = (supersubLookup.value - upperTemperatureFinalRows[0][supersubLookup.index]) / (upperTemperatureFinalRows[1][supersubLookup.index] - upperTemperatureFinalRows[0][supersubLookup.index]);
                        upperTemperatureRow = upperTemperatureFinalRows[0].map((value, index) => upperTemperatureInterpolationFactor * (upperTemperatureFinalRows[1][index] - value) + value);
                    }
                    else {
                        upperTemperatureRow = upperTemperatureFinalRows[0];
                        upperTemperatureFinalRows.push([0, 0, 0, 0, 0, 0]);
                    }

                    console.log(lowerTemperatureFinalRows);
                    console.log(upperTemperatureFinalRows);

                    const finalInterpolationFactor = (initialData.temperatureValueK - lowerTemperatureRow[1]) / (upperTemperatureRow[1] - lowerTemperatureRow[1]);

                    const finalRowData = lowerTemperatureRow.map((value, index) => finalInterpolationFactor * (upperTemperatureRow[index] - value) + value);

                    interpolationData = {
                        base: 'pressure',
                        rows: [...lowerTemperatureFinalRows, ...upperTemperatureFinalRows],
                        intermediateRows: [lowerTemperatureRow, upperTemperatureRow],
                        supersubLookup,
                        lowerInterpolationFactor: upperTemperatureInterpolationFactor,
                        upperInterpolationFactor: lowerTemperatureInterpolationFactor,
                        finalInterpolationFactor,
                        finalRowData,
                        anchor,
                        specificVolume: finalRowData[2],
                        specificInternalEnergy: finalRowData[3],
                        specificEnthalpy: finalRowData[4],
                        specificEntropy: finalRowData[5],
                    };
                }
                else if (temperatureBoundRows?.length === 1) {
                    const temperatureSet = mainTable.filter((row) => row[0] === temperatureBoundRows[0][0]);

                    if (supersubLookup) {
                        const boundingRows = findBoundingRows(temperatureSet, supersubLookup.index, supersubLookup.value);

                        if (boundingRows?.length === 2) {
                            // Temperature match, interpolate between two rows.

                            const interpolationFactor = (supersubLookup.value - boundingRows[0][supersubLookup.index]) / (boundingRows[1][supersubLookup.index] - boundingRows[0][supersubLookup.index]);
                            const finalRowData = boundingRows[0].map((value, index) => interpolationFactor * (boundingRows[1][index] - value) + value);

                            interpolationData = {
                                rows: boundingRows,
                                supersubLookup,
                                anchor,
                                interpolationFactor,
                                finalRowData,
                                specificVolume: finalRowData[2],
                                specificInternalEnergy: finalRowData[3],
                                specificEnthalpy: finalRowData[4],
                                specificEntropy: finalRowData[5],
                            };
                        }
                        else if (boundingRows?.length === 1) {
                            // Exact match found.

                            interpolationData = {
                                rows: boundingRows,
                                finalRowData: boundingRows[0],
                                specificVolume: boundingRows[0][2],
                                specificInternalEnergy: boundingRows[0][3],
                                specificEnthalpy: boundingRows[0][4],
                                specificEntropy: boundingRows[0][5],
                            };
                        }
                        else {
                            return;
                        }
                    }
                }
                else {
                    return;
                }
            }
        }

        if (interpolationData) {
            setMainInterpolationData(interpolationData);

            setPressure((initialData.pressureValueBar || interpolationData.finalRowData[0]).toPrecision(5));
            setTemperature((initialData.temperatureValueK || interpolationData.finalRowData[1]).toPrecision(5));
            setVolume(interpolationData.specificVolume.toPrecision(5));
            setEnthalpy(interpolationData.specificEnthalpy.toPrecision(5));
            setInternalEnergy(interpolationData.specificInternalEnergy.toPrecision(5));
            setEntropy(interpolationData.specificEntropy.toPrecision(5));
        }
    };

    return (
        <Sheet
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                overflowY: 'scroll',
                overflowX: 'hidden'
            }}
            data-joy-color-scheme="dark"
        >
            <Sheet
                variant="outlined"
                sx={{
                    maxWidth: '900px',
                    width: 'calc(100% - 50px)',
                    margin: '100px auto',
                    borderRadius: '10px',
                    overflowX: 'hidden',
                }}
            >
                <Box
                    sx={{
                        margin: '30px'
                    }}
                >
                    <Typography
                        sx={{
                            m: '30px 0 20px 0',
                            fontSize: '32pt',
                            textAlign: 'center',
                            fontFamily: '"Noto Sans Math"'
                        }}
                    >
                        𝕋𝕙𝕖𝕣𝕞𝕠𝕋𝕒𝕓𝕝𝕖𝕤
                    </Typography>

                    <Typography textAlign="justify">
                        This is an interpolation generator specifically designed for ME3322 'Thermodynamics' at Georgia
                        Tech. This generator uses data from the class-provided property booklets and displays all
                        math required to arrive at the final interpolated values, so you can copy the math to your page
                        and spend more time actually doing thermodynamics (rather than at least half of it doing
                        interpolations).
                    </Typography>

                    <Typography mt="10px" textAlign="justify">
                        To use this interpolator, enter either pressure or temperature, use another property to fix
                        the state, and press 'Interpolate'. Afterward, all other values will be automatically
                        populated. This interpolator handles liquid-vapor mixtures and superheated/subcooled substances.
                        The interpolator will automatically detect if a mixture is subcooled or superheated, but to
                        accurately determine values, pressure must be specified (temperature or any other property can
                        and must be used as a secondary property). It will also perform double interpolations if
                        necessary.
                    </Typography>

                    <Typography
                        sx={{
                            m: '30px 20px 20px 20px',
                            fontSize: '16pt',
                            textAlign: 'center',
                        }}
                    >
                        Input Properties
                    </Typography>

                    <FormControl>
                        <FormLabel>Substance</FormLabel>
                        <Select
                            placeholder="Choose one..."
                            onChange={(_, value) => setSubstance(value)}
                            value={substance}
                        >
                            <Option value="water">Water</Option>
                            <Option value="r22">R-22</Option>
                        </Select>
                    </FormControl>

                    <Stack direction="row" justifyContent="left" flexWrap="wrap" gap='10px' mt="15px">
                        <FormControl>
                            <FormLabel>Pressure, P<br />(bar/MPa/kPa)</FormLabel>
                            <Input
                                sx={{
                                    width: '150px'
                                }}
                                autoComplete="off"
                                onChange={(e) => setPressure(e.target.value)}
                                onKeyUp={(e) => e.key === 'Backspace' && initialData !== null && clearValues()}
                                value={pressure}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Temperature, T<br />(C/K)</FormLabel>
                            <Input
                                sx={{
                                    width: '150px'
                                }}
                                autoComplete="off"
                                onChange={(e) => setTemperature(e.target.value)}
                                onKeyUp={(e) => e.key === 'Backspace' && initialData !== null && clearValues()}
                                value={temperature}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Quality, x<br />(./%)</FormLabel>
                            <Input
                                sx={{
                                    width: '150px'
                                }}
                                autoComplete="off"
                                onChange={(e) => setQuality(e.target.value)}
                                onKeyUp={(e) => e.key === 'Backspace' && initialData !== null && clearValues()}
                                value={quality}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Specific Volume, v<br />(m3/kg)</FormLabel>
                            <Input
                                sx={{
                                    width: '150px'
                                }}
                                autoComplete="off"
                                onChange={(e) => setVolume(e.target.value)}
                                onKeyUp={(e) => e.key === 'Backspace' && initialData !== null && clearValues()}
                                value={volume}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Specific Enthalpy, h<br />(kJ/kg)</FormLabel>
                            <Input
                                sx={{
                                    width: '150px'
                                }}
                                autoComplete="off"
                                onChange={(e) => setEnthalpy(e.target.value)}
                                onKeyUp={(e) => e.key === 'Backspace' && initialData !== null && clearValues()}
                                value={enthalpy}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Specific Int. Energy, u<br />(kJ/kg)</FormLabel>
                            <Input
                                sx={{
                                    width: '150px'
                                }}
                                autoComplete="off"
                                onChange={(e) => setInternalEnergy(e.target.value)}
                                onKeyUp={(e) => e.key === 'Backspace' && initialData !== null && clearValues()}
                                value={internalEnergy}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Specific Entropy, s<br />(kJ/kgK)</FormLabel>
                            <Input
                                sx={{
                                    width: '150px'
                                }}
                                autoComplete="off"
                                onChange={(e) => setEntropy(e.target.value)}
                                onKeyUp={(e) => e.key === 'Backspace' && initialData !== null && clearValues()}
                                value={entropy}
                            />
                        </FormControl>
                    </Stack>

                    <Button
                        onClick={runInterpolation}
                        sx={{
                            mt: '20px',
                            width: '150px',
                            margin: '20px calc((100% - 150px) / 2)'
                        }}
                    >
                        Interpolate
                    </Button>

                    {initialData !== null && (
                        <>
                            {initialData.originalRows.length > 1 ? (
                                <Box>
                                    <Typography
                                        sx={{
                                            mb: '20px'
                                        }}
                                    >
                                        Initial interpolation between two rows:
                                    </Typography>
                                    <Box
                                        sx={{
                                            overflowX: 'scroll',
                                        }}
                                    >
                                        <StaticMathField style={{ display: 'block' }}>
                                            {`\\mathrm{property} = \\chi\\cdot(\\mathrm{property}_2 - \\mathrm{property}_1) + \\mathrm{property}_1`}
                                        </StaticMathField>
                                        <StaticMathField style={{ display: 'block' }}>
                                            {`\\chi = \\frac{P - P_1}{P_2 - P_1} = \\frac{${initialData.pressureValueBar} - ${initialData.originalRows[0][0]}}{${initialData.originalRows[1][0]} - ${initialData.originalRows[0][0]}} = ${initialData.initialInterpolationFactor.toPrecision(5)}`}
                                        </StaticMathField>
                                        <Table
                                            sx={{
                                                overflowX: 'scroll',
                                            }}
                                        >
                                            <thead>
                                            <tr>
                                                <th>P</th>
                                                <th>T</th>
                                                <th>vf</th>
                                                <th>vg</th>
                                                <th>uf</th>
                                                <th>ug</th>
                                                <th>hf</th>
                                                <th>hg</th>
                                                <th>sf</th>
                                                <th>sg</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {initialData.originalRows.map((row, rowIndex) => (
                                                <tr key={rowIndex}>
                                                    {row.map((value, colIndex) => (<td key={rowIndex + '' + colIndex}>{value}</td>))}
                                                </tr>
                                            ))}
                                            <tr>
                                                {initialData.finalRowData.map((value) => (<td key={value}><b>{value.toPrecision(5)}</b></td>))}
                                            </tr>
                                            </tbody>
                                        </Table>
                                    </Box>
                                </Box>
                            ) : (
                                <>
                                    <Typography
                                        sx={{
                                            mb: '20px'
                                        }}
                                    >
                                        Using the following values:
                                    </Typography>
                                    <Box
                                        sx={{
                                            overflowX: 'scroll',
                                        }}
                                    >
                                        <Table
                                            sx={{
                                                minWidth: '600px',
                                            }}
                                        >
                                            <thead>
                                                <tr>
                                                    <th>P</th>
                                                    <th>T</th>
                                                    <th>vf</th>
                                                    <th>vg</th>
                                                    <th>uf</th>
                                                    <th>ug</th>
                                                    <th>hf</th>
                                                    <th>hg</th>
                                                    <th>sf</th>
                                                    <th>sg</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    {initialData.finalRowData.map((value) => (<td key={value}>{Math.round(value * 10000) / 10000}</td>))}
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Box>
                                </>
                            )}
                            <Typography mb="20px">
                                <b>State: </b> {
                                state === 1 ? 'Superheated' :
                                    (state === -1 ? 'Subcooled' :
                                        'Liquid-Vapor Mixture')
                            }
                            </Typography>
                        </>
                    )}

                    {state !== 0 && mainInterpolationData && (
                        <>
                            <Typography>Found the following superheated/subcooled values surrounding the input values:</Typography>
                            <Box
                                sx={{
                                    overflowX: 'scroll',
                                }}
                            >
                                <Table
                                    sx={{
                                        minWidth: '500px',
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            <th>P</th>
                                            <th>T</th>
                                            <th>v</th>
                                            <th>u</th>
                                            <th>h</th>
                                            <th>s</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mainInterpolationData.rows.map((row, rowIndex) => (
                                            <tr key={rowIndex}>
                                                {row.map((value, colIndex) => (<td key={rowIndex + '' + colIndex}>{value}</td>))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Box>
                            {mainInterpolationData.rows.length === 2 && (
                                <>
                                    <Box
                                        sx={{
                                            overflowX: 'scroll',
                                        }}
                                    >
                                        <StaticMathField style={{ display: 'block', marginTop: '10px' }}>
                                            {`\\chi = ` + (mainInterpolationData.anchor.temperature !== undefined ? `\\frac{T - T_1}{T_2 - T_1} = \\frac{${mainInterpolationData.anchor.temperature} - ${mainInterpolationData.rows[0][1]}}{${mainInterpolationData.rows[1][1]} - ${mainInterpolationData.rows[0][1]}}` :
                                                mainInterpolationData.anchor.pressure !== undefined ? `\\frac{P - P_1}{P_2 - P_1} = \\frac{${mainInterpolationData.anchor.pressure} - ${mainInterpolationData.rows[0][0]}}{${mainInterpolationData.rows[1][0]} - ${mainInterpolationData.rows[0][0]}}` :
                                                mainInterpolationData.anchor.specificVolume !== undefined ? `\\frac{v - v_1}{v_2 - v_1} = \\frac{${mainInterpolationData.anchor.specificVolume} - ${mainInterpolationData.rows[0][2]}}{${mainInterpolationData.rows[1][2]} - ${mainInterpolationData.rows[0][2]}}` :
                                                mainInterpolationData.anchor.specificInternalEnergy !== undefined ? `\\frac{u - u_1}{u_2 - u_1} = \\frac{${mainInterpolationData.anchor.specificInternalEnergy} - ${mainInterpolationData.rows[0][3]}}{${mainInterpolationData.rows[1][3]} - ${mainInterpolationData.rows[0][3]}}` :
                                                mainInterpolationData.anchor.specificEnthalpy !== undefined ? `\\frac{h - h_1}{h_2 - h_1} = \\frac{${mainInterpolationData.anchor.specificEnthalpy} - ${mainInterpolationData.rows[0][4]}}{${mainInterpolationData.rows[1][4]} - ${mainInterpolationData.rows[0][4]}}` :
                                                mainInterpolationData.anchor.specificEntropy !== undefined ? `\\frac{s - s_1}{s_2 - s_1} = \\frac{${mainInterpolationData.anchor.specificEntropy} - ${mainInterpolationData.rows[0][5]}}{${mainInterpolationData.rows[1][5]} - ${mainInterpolationData.rows[0][5]}}` :
                                                ``
                                            ) + ` = ${mainInterpolationData.interpolationFactor.toPrecision(5)}`}
                                        </StaticMathField>
                                    </Box>
                                    <Box
                                        sx={{
                                            overflowX: 'scroll',
                                        }}
                                    >
                                        <table
                                            sx={{
                                                minWidth: '600px'
                                            }}
                                        >
                                            <thead>
                                            <tr>
                                                <th style={{width: '150px'}}/>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr>
                                                <td>
                                                    <Typography>
                                                        Pressure
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <StaticMathField>
                                                        {`P = ${mainInterpolationData.finalRowData[0].toPrecision(5)} \\text{ bar} = ${(mainInterpolationData.finalRowData[0] / 10).toPrecision(5)} \\text{ MPa} = ${(mainInterpolationData.finalRowData[0] * 10).toPrecision(5)} \\text{ kPa}`}
                                                    </StaticMathField>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography>
                                                        Temperature
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <StaticMathField>
                                                        {`T = ${mainInterpolationData.finalRowData[1].toPrecision(5)} \\text{ K} = ${(mainInterpolationData.finalRowData[1] - 273.15).toPrecision(5)} \\degree\\text{ C}`}
                                                    </StaticMathField>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography>
                                                        Specific Volume
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <StaticMathField>
                                                        {`v = \\chi (v_2 - v_1) + v_1 = (${mainInterpolationData.interpolationFactor.toPrecision(5)})(${mainInterpolationData.rows[1][2].toPrecision(5)} - ${mainInterpolationData.rows[0][2].toPrecision(5)}) + ${mainInterpolationData.rows[0][2]} = ${mainInterpolationData.specificVolume.toPrecision(5)}\\text{ }\\mathrm{\\frac{m^3}{kg}}=v`}
                                                    </StaticMathField>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography>
                                                        Specific Internal Energy
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <StaticMathField>
                                                        {`u = \\chi (u_2 - u_1) + u_1 = (${mainInterpolationData.interpolationFactor.toPrecision(5)})(${mainInterpolationData.rows[1][3].toPrecision(5)} - ${mainInterpolationData.rows[0][3].toPrecision(5)}) + ${mainInterpolationData.rows[0][3]} = ${mainInterpolationData.specificInternalEnergy.toPrecision(5)}\\text{ }\\mathrm{\\frac{kJ}{kg}}=u`}
                                                    </StaticMathField>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography>
                                                        Specific Enthalpy
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <StaticMathField>
                                                        {`h = \\chi (h_2 - h_1) + h_1 = (${mainInterpolationData.interpolationFactor.toPrecision(5)})(${mainInterpolationData.rows[1][4].toPrecision(5)} - ${mainInterpolationData.rows[0][4].toPrecision(5)}) + ${mainInterpolationData.rows[0][4]} = ${mainInterpolationData.specificEnthalpy.toPrecision(5)}\\text{ }\\mathrm{\\frac{kJ}{kg}}=h`}
                                                    </StaticMathField>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography>
                                                        Specific Entropy
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <StaticMathField>
                                                        {`s = \\chi (s_2 - s_1) + s_1 = (${mainInterpolationData.interpolationFactor.toPrecision(5)})(${mainInterpolationData.rows[1][5].toPrecision(5)} - ${mainInterpolationData.rows[0][5].toPrecision(5)}) + ${mainInterpolationData.rows[0][5]} = ${mainInterpolationData.specificEntropy.toPrecision(5)}\\text{ }\\mathrm{\\frac{kJ}{kg \\cdot K}}=s`}
                                                    </StaticMathField>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </Box>
                                </>
                            )}
                            {mainInterpolationData.rows.length === 4 && (
                                <>
                                    <Box
                                        sx={{
                                            overflowX: 'scroll'
                                        }}
                                    >
                                        <StaticMathField style={{ display: 'block', marginTop: '10px' }}>
                                            {`\\chi_1 = ` + (mainInterpolationData.anchor.temperature !== undefined ? `\\frac{T - T_1}{T_2 - T_1} = \\frac{${mainInterpolationData.anchor.temperature} - ${mainInterpolationData.rows[0][1]}}{${mainInterpolationData.rows[1][1]} - ${mainInterpolationData.rows[0][1]}}` :
                                                    mainInterpolationData.anchor.pressure !== undefined ? `\\frac{P - P_1}{P_2 - P_1} = \\frac{${mainInterpolationData.anchor.pressure} - ${mainInterpolationData.rows[0][0]}}{${mainInterpolationData.rows[1][0]} - ${mainInterpolationData.rows[0][0]}}` :
                                                    mainInterpolationData.anchor.specificVolume !== undefined ? `\\frac{v - v_1}{v_2 - v_1} = \\frac{${mainInterpolationData.anchor.specificVolume} - ${mainInterpolationData.rows[0][2]}}{${mainInterpolationData.rows[1][2]} - ${mainInterpolationData.rows[0][2]}}` :
                                                    mainInterpolationData.anchor.specificInternalEnergy !== undefined ? `\\frac{u - u_1}{u_2 - u_1} = \\frac{${mainInterpolationData.anchor.specificInternalEnergy} - ${mainInterpolationData.rows[0][3]}}{${mainInterpolationData.rows[1][3]} - ${mainInterpolationData.rows[0][3]}}` :
                                                    mainInterpolationData.anchor.specificEnthalpy !== undefined ? `\\frac{h - h_1}{h_2 - h_1} = \\frac{${mainInterpolationData.anchor.specificEnthalpy} - ${mainInterpolationData.rows[0][4]}}{${mainInterpolationData.rows[1][4]} - ${mainInterpolationData.rows[0][4]}}` :
                                                    mainInterpolationData.anchor.specificEntropy !== undefined ? `\\frac{s - s_1}{s_2 - s_1} = \\frac{${mainInterpolationData.anchor.specificEntropy} - ${mainInterpolationData.rows[0][5]}}{${mainInterpolationData.rows[1][5]} - ${mainInterpolationData.rows[0][5]}}` :
                                                    ``
                                            ) + ` = ${mainInterpolationData.upperInterpolationFactor.toPrecision(5)}`}
                                        </StaticMathField>
                                        <StaticMathField style={{ display: 'block', marginTop: '10px' }}>
                                            {`\\chi_2 = ` + (mainInterpolationData.anchor.temperature !== undefined ? `\\frac{T - T_1}{T_2 - T_1} = \\frac{${mainInterpolationData.anchor.temperature} - ${mainInterpolationData.rows[2][1]}}{${mainInterpolationData.rows[2][1]} - ${mainInterpolationData.rows[2][1]}}` :
                                                    mainInterpolationData.anchor.pressure !== undefined ? `\\frac{P - P_1}{P_2 - P_1} = \\frac{${mainInterpolationData.anchor.pressure} - ${mainInterpolationData.rows[2][0]}}{${mainInterpolationData.rows[3][0]} - ${mainInterpolationData.rows[2][0]}}` :
                                                    mainInterpolationData.anchor.specificVolume !== undefined ? `\\frac{v - v_1}{v_2 - v_1} = \\frac{${mainInterpolationData.anchor.specificVolume} - ${mainInterpolationData.rows[2][2]}}{${mainInterpolationData.rows[3][2]} - ${mainInterpolationData.rows[2][2]}}` :
                                                    mainInterpolationData.anchor.specificInternalEnergy !== undefined ? `\\frac{u - u_1}{u_2 - u_1} = \\frac{${mainInterpolationData.anchor.specificInternalEnergy} - ${mainInterpolationData.rows[2][3]}}{${mainInterpolationData.rows[3][3]} - ${mainInterpolationData.rows[2][3]}}` :
                                                    mainInterpolationData.anchor.specificEnthalpy !== undefined ? `\\frac{h - h_1}{h_2 - h_1} = \\frac{${mainInterpolationData.anchor.specificEnthalpy} - ${mainInterpolationData.rows[2][4]}}{${mainInterpolationData.rows[3][4]} - ${mainInterpolationData.rows[2][4]}}` :
                                                    mainInterpolationData.anchor.specificEntropy !== undefined ? `\\frac{s - s_1}{s_2 - s_1} = \\frac{${mainInterpolationData.anchor.specificEntropy} - ${mainInterpolationData.rows[2][5]}}{${mainInterpolationData.rows[3][5]} - ${mainInterpolationData.rows[2][5]}}` :
                                                    ``
                                            ) + ` = ${mainInterpolationData.lowerInterpolationFactor.toPrecision(5)}`}
                                        </StaticMathField>
                                    </Box>
                                    <Typography mt="10px" mb="10px">
                                        With these values, the intermediate rows may be calculated:
                                    </Typography>
                                    <Box
                                        sx={{
                                            overflowX: 'scroll',
                                        }}
                                    >
                                        <StaticMathField>
                                            {`\\mathrm{property}_{i} = \\chi_i (\\mathrm{property}_{i,2} - \\mathrm{property}_{i,1}) + \\mathrm{property}_{i,1}`}
                                        </StaticMathField>
                                    </Box>
                                    <Box
                                        sx={{
                                            overflowX: 'scroll',
                                        }}
                                    >
                                        <Table
                                            sx={{
                                                minWidth: '500px',
                                            }}
                                        >
                                            <thead>
                                            <tr>
                                                <th>P</th>
                                                <th>T</th>
                                                <th>v</th>
                                                <th>u</th>
                                                <th>h</th>
                                                <th>s</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {mainInterpolationData.intermediateRows.map((row, rowIndex) => (
                                                <tr key={rowIndex}>
                                                    {row.map((value, colIndex) => (<td key={rowIndex + '' + colIndex}>{value.toPrecision(5)}</td>))}
                                                </tr>
                                            ))}
                                            </tbody>
                                        </Table>
                                    </Box>
                                    <Typography mt="10px" mb="10px">
                                        The final interpolation can be done:
                                    </Typography>
                                    <Box
                                        sx={{
                                            overflowX: 'scroll',
                                        }}
                                    >
                                        <StaticMathField>
                                            {mainInterpolationData.base === 'pressure' ?
                                                `\\chi_0 = \\frac{P-P_1}{P_2-P_1} = \\frac{${initialData.pressureValueBar} - ${mainInterpolationData.intermediateRows[0][0]}}{${mainInterpolationData.intermediateRows[1][0]} - ${mainInterpolationData.intermediateRows[0][0]}} = ${mainInterpolationData.finalInterpolationFactor}` :
                                                `\\chi_0 = \\frac{T-T_1}{T_2-T_1} = \\frac{${initialData.temperatureValueK} - ${mainInterpolationData.intermediateRows[0][1]}}{${mainInterpolationData.intermediateRows[1][1]} - ${mainInterpolationData.intermediateRows[0][1]}} = ${mainInterpolationData.finalInterpolationFactor}`}
                                        </StaticMathField>
                                        <table>
                                            <thead>
                                            <tr>
                                                <th style={{width: '150px'}}/>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr>
                                                <td>
                                                    <Typography>
                                                        Pressure
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <StaticMathField>
                                                        {initialData.pressureValueBar ?
                                                            `P = ${initialData.pressureValueBar.toPrecision(5)} \\text{ bar} = ${(initialData.pressureValueBar / 10).toPrecision(5)} \\text{ MPa} = ${(initialData.pressureValueBar * 10).toPrecision(5)} \\text{ kPa}` :
                                                            `P = \\chi_0 (P_2 - P_1) + P_1 = (${mainInterpolationData.finalInterpolationFactor.toPrecision(5)})(${mainInterpolationData.intermediateRows[1][0].toPrecision(5)} - ${mainInterpolationData.intermediateRows[0][0].toPrecision(5)}) + ${mainInterpolationData.intermediateRows[0][0].toPrecision(5)} = ${mainInterpolationData.finalRowData[0].toPrecision(5)} \\text{ bar} = ${(mainInterpolationData.finalRowData[0] / 10).toPrecision(5)} \\text{ MPa} = ${(mainInterpolationData.finalRowData[0] * 10).toPrecision(5)} \\text{ kPa}`}
                                                    </StaticMathField>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography>
                                                        Temperature
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <StaticMathField>
                                                        {initialData.temperatureValueK ?
                                                            `T = ${initialData.temperatureValueK.toPrecision(5)} \\text{ K} = ${(initialData.temperatureValueK - 273.15).toPrecision(5)} \\degree\\text{ C}` :
                                                            `T = \\chi_0 (T_2 - T_1) + T_1 = (${mainInterpolationData.finalInterpolationFactor.toPrecision(5)})(${mainInterpolationData.intermediateRows[1][1].toPrecision(5)} - ${mainInterpolationData.intermediateRows[0][1].toPrecision(5)}) + ${mainInterpolationData.intermediateRows[0][1].toPrecision(5)} = ${mainInterpolationData.finalRowData[1].toPrecision(5)} \\text{ K} = ${(mainInterpolationData.finalRowData[1] - 273.15).toPrecision(5)} \\degree\\text{ C}`}
                                                    </StaticMathField>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography>
                                                        Specific Volume
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <StaticMathField>
                                                        {mainInterpolationData.anchor.volume ?
                                                            `v = ${mainInterpolationData.anchor.volume.toPrecision(5)}\\text{ }\\mathrm{\\frac{m^3}{kg}}=v` :
                                                            `v = \\chi_0 (v_2 - v_1) + v_1 = (${mainInterpolationData.finalInterpolationFactor.toPrecision(5)})(${mainInterpolationData.intermediateRows[1][2].toPrecision(5)} - ${mainInterpolationData.intermediateRows[0][2].toPrecision(5)}) + ${mainInterpolationData.intermediateRows[0][2].toPrecision(5)} = ${mainInterpolationData.specificVolume.toPrecision(5)}\\text{ }\\mathrm{\\frac{m^3}{kg}}=v`}
                                                    </StaticMathField>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography>
                                                        Specific Internal Energy
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <StaticMathField>
                                                        {mainInterpolationData.anchor.internalEnergy ?
                                                            `u = ${mainInterpolationData.anchor.internalEnergy.toPrecision(5)}\\text{ }\\mathrm{\\frac{kJ}{kg}}=u` :
                                                            `u = \\chi_0 (u_2 - u_1) + u_1 = (${mainInterpolationData.finalInterpolationFactor.toPrecision(5)})(${mainInterpolationData.intermediateRows[1][3].toPrecision(5)} - ${mainInterpolationData.intermediateRows[0][3].toPrecision(5)}) + ${mainInterpolationData.intermediateRows[0][3].toPrecision(5)} = ${mainInterpolationData.specificInternalEnergy.toPrecision(5)}\\text{ }\\mathrm{\\frac{kJ}{kg}}=u`}
                                                    </StaticMathField>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography>
                                                        Specific Enthalpy
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <StaticMathField>
                                                        {mainInterpolationData.anchor.enthalpy ?
                                                            `h = ${mainInterpolationData.anchor.enthalpy.toPrecision(5)}\\text{ }\\mathrm{\\frac{kJ}{kg}}=h` :
                                                            `h = \\chi_0 (h_2 - h_1) + h_1 = (${mainInterpolationData.finalInterpolationFactor.toPrecision(5)})(${mainInterpolationData.intermediateRows[1][4].toPrecision(5)} - ${mainInterpolationData.intermediateRows[0][4].toPrecision(5)}) + ${mainInterpolationData.intermediateRows[0][4].toPrecision(5)} = ${mainInterpolationData.specificEnthalpy.toPrecision(5)}\\text{ }\\mathrm{\\frac{kJ}{kg}}=h`}
                                                    </StaticMathField>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Typography>
                                                        Specific Entropy
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <StaticMathField>
                                                        {mainInterpolationData.anchor.entropy ?
                                                            `s = ${mainInterpolationData.anchor.entropy.toPrecision(5)}\\text{ }\\mathrm{\\frac{kJ}{kg \\cdot K}}=s` :
                                                            `s = \\chi_0 (s_2 - s_1) + s_1 = (${mainInterpolationData.finalInterpolationFactor.toPrecision(5)})(${mainInterpolationData.intermediateRows[1][5].toPrecision(5)} - ${mainInterpolationData.intermediateRows[0][5].toPrecision(5)}) + ${mainInterpolationData.intermediateRows[0][5].toPrecision(5)} = ${mainInterpolationData.specificEntropy.toPrecision(5)}\\text{ }\\mathrm{\\frac{kJ}{kg \\cdot K}}=s`}
                                                    </StaticMathField>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </Box>
                                </>
                            )}
                        </>
                    )}

                    {state === 0 && (
                        <>
                            {mainInterpolationData &&
                                <Box
                                    sx={{
                                        overflowX: 'scroll',
                                    }}
                                >
                                    <table>
                                        <thead>
                                        <tr>
                                            <th style={{width: '150px'}}/>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr>
                                            <td>
                                                <Typography>
                                                    Pressure
                                                </Typography>
                                            </td>
                                            <td>
                                                <StaticMathField>
                                                    {`P = ${initialData.finalRowData[0]} \\text{ bar} = ${(initialData.finalRowData[0] / 10).toPrecision(5)} \\text{ MPa} = ${(initialData.finalRowData[0] * 10).toPrecision(5)} \\text{ kPa}`}
                                                </StaticMathField>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <Typography>
                                                    Temperature
                                                </Typography>
                                            </td>
                                            <td>
                                                <StaticMathField>
                                                    {`T = ${initialData.finalRowData[1]} \\text{ K} = ${(initialData.finalRowData[1] - 273.15).toPrecision(5)} \\degree\\text{ C}`}
                                                </StaticMathField>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <Typography>
                                                    Quality
                                                </Typography>
                                            </td>
                                            <td>
                                                <StaticMathField>
                                                    {
                                                        (mainInterpolationData.anchor.quality && `x = ${mainInterpolationData.qualityValue}`) ||
                                                        (mainInterpolationData.anchor.volume && `x = \\frac{v - v_f}{v_g - v_f} = \\frac{${mainInterpolationData.anchor.volume} - ${initialData.finalRowData[2]}}{${initialData.finalRowData[3]} - ${initialData.finalRowData[2]}} = ${mainInterpolationData.qualityValue.toPrecision(5)}`) ||
                                                        (mainInterpolationData.anchor.specificInternalEnergy && `x = \\frac{u - u_f}{u_g - u_f} = \\frac{${mainInterpolationData.anchor.specificInternalEnergy} - ${initialData.finalRowData[4]}}{${initialData.finalRowData[5]} - ${initialData.finalRowData[4]}} = ${mainInterpolationData.qualityValue.toPrecision(5)}`) ||
                                                        (mainInterpolationData.anchor.specificEnthalpy && `x = \\frac{h - h_f}{h_g - h_f} = \\frac{${mainInterpolationData.anchor.specificEnthalpy} - ${initialData.finalRowData[6]}}{${initialData.finalRowData[7]} - ${initialData.finalRowData[6]}} = ${mainInterpolationData.qualityValue.toPrecision(5)}`) ||
                                                        (mainInterpolationData.anchor.specificEntropy && `x = \\frac{s - s_f}{s_g - s_f} = \\frac{${mainInterpolationData.anchor.specificEntropy} - ${initialData.finalRowData[8]}}{${initialData.finalRowData[9]} - ${initialData.finalRowData[8]}} = ${mainInterpolationData.qualityValue.toPrecision(5)}`)
                                                    }
                                                </StaticMathField>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <Typography>
                                                    Specific Volume
                                                </Typography>
                                            </td>
                                            <td>
                                                <StaticMathField>
                                                    {`v = x (v_g - v_f) + v_f = (${mainInterpolationData.qualityValue.toPrecision(5)})(${initialData.finalRowData[3].toPrecision(5)} - ${initialData.finalRowData[2].toPrecision(5)}) + ${initialData.finalRowData[2].toPrecision(5)} = ${mainInterpolationData.specificVolume.toPrecision(5)}\\text{ }\\mathrm{\\frac{m^3}{kg}}=v`}
                                                </StaticMathField>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <Typography>
                                                    Specific Internal Energy
                                                </Typography>
                                            </td>
                                            <td>
                                                <StaticMathField>
                                                    {`u = x (u_g - u_f) + u_f = (${mainInterpolationData.qualityValue.toPrecision(5)})(${initialData.finalRowData[5].toPrecision(5)} - ${initialData.finalRowData[4].toPrecision(5)}) + ${initialData.finalRowData[4].toPrecision(5)} = ${mainInterpolationData.specificInternalEnergy.toPrecision(5)}\\text{ }\\mathrm{\\frac{kJ}{kg}}=u`}
                                                </StaticMathField>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <Typography>
                                                    Specific Enthalpy
                                                </Typography>
                                            </td>
                                            <td>
                                                <StaticMathField>
                                                    {`h = x (h_g - h_f) + h_f = (${mainInterpolationData.qualityValue.toPrecision(5)})(${initialData.finalRowData[7].toPrecision(5)} - ${initialData.finalRowData[6].toPrecision(5)}) + ${initialData.finalRowData[6].toPrecision(5)} = ${mainInterpolationData.specificEnthalpy.toPrecision(5)}\\text{ }\\mathrm{\\frac{kJ}{kg}}=h`}
                                                </StaticMathField>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <Typography>
                                                    Specific Entropy
                                                </Typography>
                                            </td>
                                            <td>
                                                <StaticMathField>
                                                    {`s = x (s_g - s_f) + s_f = (${mainInterpolationData.qualityValue.toPrecision(5)})(${initialData.finalRowData[9].toPrecision(5)} - ${initialData.finalRowData[8].toPrecision(5)}) + ${initialData.finalRowData[8].toPrecision(5)} = ${mainInterpolationData.specificEntropy.toPrecision(5)}\\text{ }\\mathrm{\\frac{kJ}{kg\\cdot K}}=s`}
                                                </StaticMathField>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </Box>
                            }
                        </>
                    )}
                </Box>
            </Sheet>
        </Sheet>
    );
}